import { useQuery } from '@tanstack/react-query';
import { getProviderVettingStatus, getProviderVettingStatusBatch } from '../api/vetting.api';
import type { ProviderVettingSummary } from '../types/vetting.types';

export const vettingKeys = {
  all: ['vetting'] as const,
  status: (providerId: string) => [...vettingKeys.all, 'status', providerId] as const,
  statusBatch: (providerIds: string[]) => [...vettingKeys.all, 'status-batch', ...providerIds] as const,
};

const POLL_INTERVAL_MS = 15000;

/**
 * Real API version — replaces the mock/vetting.store.ts-backed implementation.
 * Same return shape (`ProviderVettingSummary | null`) so callers (VettingCheckList,
 * ProviderVettingStatusCell) don't need to change. Polls while unresolved so an
 * admin watching the Pending tab sees a Checkr webhook result land without a
 * manual refresh; stops polling once approvable (nothing left to change).
 */
/** Returns `undefined` while the first load is in flight (show a skeleton), `null` when disabled or failed. */
export function useProviderVettingStatus(
  providerId: string | undefined,
): ProviderVettingSummary | null | undefined {
  const { data, isLoading } = useQuery({
    queryKey: vettingKeys.status(providerId ?? ''),
    queryFn: () => getProviderVettingStatus(providerId as string),
    enabled: !!providerId,
    refetchInterval: (query) => {
      const summary = query.state.data as ProviderVettingSummary | undefined;
      return summary && !summary.approvable ? POLL_INTERVAL_MS : false;
    },
  });

  if (isLoading) return undefined;
  return data ?? null;
}

/**
 * Batched version for list views (e.g. the Active Providers table) — one
 * request for every provider id currently on screen, instead of one request
 * per row. Returns a map keyed by providerId; a row with no entry yet (still
 * loading, or not in the batch) should treat itself as unknown, same as
 * useProviderVettingStatus returning null.
 */
export type ProviderVettingStatusBatch = {
  summaries: Record<string, ProviderVettingSummary>;
  /** The batch request failed (after retries) — callers must not keep showing skeletons. */
  isError: boolean;
  refetch: () => void;
};

export function useProviderVettingStatusBatch(providerIds: string[]): ProviderVettingStatusBatch {
  const { data, isError, refetch } = useQuery({
    queryKey: vettingKeys.statusBatch(providerIds),
    queryFn: () => getProviderVettingStatusBatch(providerIds),
    enabled: providerIds.length > 0,
  });

  return { summaries: data ?? {}, isError, refetch: () => void refetch() };
}
